import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';

import ProjectCard from './components/ProjectCard';
import ProjectFilters from './components/ProjectFilters';
import ProjectStats from './components/ProjectStats';
import EmptyState from './components/EmptyState';
import { projectService } from '../../services/projectService';

const AllProjects = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: '',
    sort: 'newest'
  });

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load projects from Supabase
  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      try {
        const data = await projectService?.getAll();
        if (isMounted) {
          setProjects(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = projectService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProjects(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setProjects(prev => prev?.map(p => 
          p?.id === payload?.new?.id ? { ...p, ...payload?.new } : p
        ));
      } else if (payload?.eventType === 'DELETE') {
        setProjects(prev => prev?.filter(p => p?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = projects?.filter(project => {
      // Search filter
      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        const matchesName = project?.name?.toLowerCase()?.includes(searchLower);
        const matchesVendor = project?.vendors?.some(vendor => 
          vendor?.toLowerCase()?.includes(searchLower)
        );
        if (!matchesName && !matchesVendor) return false;
      }

      // Category filter
      if (filters?.category !== 'all' && project?.category !== filters?.category) {
        return false;
      }

      // Status filter
      if (filters?.status !== 'all' && project?.status !== filters?.status) {
        return false;
      }

      // Date range filter
      if (filters?.dateFrom) {
        const projectDate = new Date(project.createdDate);
        const fromDate = new Date(filters.dateFrom);
        if (projectDate < fromDate) return false;
      }

      if (filters?.dateTo) {
        const projectDate = new Date(project.createdDate);
        const toDate = new Date(filters.dateTo);
        if (projectDate > toDate) return false;
      }

      return true;
    });

    // Sort projects
    filtered?.sort((a, b) => {
      switch (filters?.sort) {
        case 'oldest':
          return new Date(a.createdDate) - new Date(b.createdDate);
        case 'deadline':
          if (!a?.deadline && !b?.deadline) return 0;
          if (!a?.deadline) return 1;
          if (!b?.deadline) return -1;
          return new Date(a.deadline) - new Date(b.deadline);
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'newest':
        default:
          return new Date(b.createdDate) - new Date(a.createdDate);
      }
    });

    return filtered;
  }, [filters, projects]);

  // Calculate project counts for filters
  const projectCounts = useMemo(() => {
    const counts = {
      cars: 0,
      homes: 0,
      refinancing: 0,
      furniture: 0,
      electronics: 0,
      active: 0,
      pending: 0,
      completed: 0
    };

    projects?.forEach(project => {
      counts[project.category]++;
      counts[project.status]++;
    });

    return counts;
  }, [projects]);

  // Calculate stats
  const stats = useMemo(() => {
    const activeProjects = projects?.filter(p => p?.status === 'active')?.length;
    const pendingQuotes = projects?.reduce((sum, p) => 
      sum + (p?.vendorCount - p?.quotesReceived), 0
    );
    const completedProjects = projects?.filter(p => p?.status === 'completed')?.length;
    
    const today = new Date();
    const upcomingDeadlines = projects?.filter(p => p?.status === 'draft')?.length;

    return {
      activeProjects,
      pendingQuotes,
      completedProjects,
      upcomingDeadlines
    };
  }, [projects]);

  const handleCreateProject = () => {
    navigate('/create-project-flow');
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this stack? This action cannot be undone.')) return;
    try {
      await projectService?.delete(projectId);
      setProjects(prev => prev?.filter(p => p?.id !== projectId));
    } catch (err) {
      setError('Failed to delete stack: ' + err?.message);
    }
  };

  const handleActiveProjectsClick = () => {
    setFilters(prevFilters => ({
      ...prevFilters,
      status: 'active',
      category: 'all'
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      status: 'all',
      dateFrom: '',
      dateTo: '',
      sort: 'newest'
    });
  };

  const hasActiveFilters = () => {
    return filters?.category !== 'all' || 
           filters?.status !== 'all' || 
           filters?.search !== '' ||
           filters?.dateFrom !== '' ||
           filters?.dateTo !== '';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32">
        {/* Gradient Hero Header */}
        <div
          className="relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8"
          style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 -left-8 w-40 h-40 rounded-full bg-white/5 blur-2xl" />
            {/* Decorative category icons */}
            <div className="absolute top-4 right-24 opacity-20 rotate-12">
              <div className="w-12 h-12 rounded-2xl bg-white/25 border border-white/35 flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
            </div>
            <div className="absolute bottom-4 right-48 opacity-20 -rotate-6">
              <div className="w-10 h-10 rounded-2xl bg-white/25 border border-white/35 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              </div>
            </div>
          </div>
          <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <Breadcrumb />
              <h1 className="text-3xl md:text-4xl font-bold text-white mt-1 mb-2">My Stacks</h1>
              <p className="text-white/75 text-base">Manage your purchase stacks and compare vendor offers</p>
            </div>
            <button
              onClick={handleCreateProject}
              className="flex items-center gap-2.5 px-6 py-3.5 bg-white font-semibold rounded-xl hover:bg-white/90 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 self-start lg:self-auto"
              style={{ color: '#F06F6B' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Create New Stack
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <ProjectStats
            stats={stats}
            onActiveProjectsClick={handleActiveProjectsClick}
          />

          {/* Filters */}
          <ProjectFilters
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            projectCounts={projectCounts}
          />

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center animate-pulse"
                  style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                </div>
                <p className="text-text-secondary text-sm font-medium">Loading your stacks...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-5 bg-error/10 border border-error/20 rounded-2xl mb-6">
              <p className="text-error text-sm font-medium">Error loading stacks: {error}</p>
            </div>
          )}

          {/* Projects Grid */}
          {!loading && !error && (
            <>
              {filteredProjects?.length === 0 ? (
                <EmptyState
                  hasFilters={hasActiveFilters()}
                  onClearFilters={handleClearFilters}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredProjects?.map((project) => (
                    <ProjectCard key={project?.id} project={project} onDelete={handleDeleteProject} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AllProjects;